import { getSportFullBoardType, Sport } from "@sportactics/board-editing";
import { create } from "zustand";

import {
  AnalyticsEventType,
  SignInEntryPoint,
  useAnalyticsEvent,
} from "../analytics-event";
import { useSelectBoardType } from "../select-board-type";
import {
  BoardPurpose,
  BoardVenue,
  useMutateUser,
  User,
  UserRole,
  useUser,
} from "../user";
import { OnboardingUserType } from "./onboarding-user-type";

// TODO: Refactor
export function useOnboarding(): useOnboardingReturn {
  const {
    answer: { role },
  } = useStore();
  const onboardingRequired = useRequired();
  const selectors = useSelectors();

  return { onboardingRequired, role, ...selectors };
}

// TODO
// eslint-disable-next-line max-lines-per-function
function useSelectors(): Pick<
  useOnboardingReturn,
  | "selectUserType"
  | "selectSport"
  | "selectRole"
  | "selectBoardPurposes"
  | "selectBoardVenues"
> {
  const { answer, setAnswer } = useStore();
  const { submitEvent } = useAnalyticsEvent();
  const complete = useComplete();

  return {
    selectUserType: (userType: OnboardingUserType) => {
      submitEvent({
        type: AnalyticsEventType.OnboardingUserTypeSelected,
        onboardingUserType: userType,
      });
      // Answering "returning" is what opens the sign-in UI here, so it is also the
      // click that starts the funnel this entry point is measured by.
      if (userType === OnboardingUserType.Returning)
        submitEvent({
          type: AnalyticsEventType.ClickSignIn,
          signInEntryPoint: SignInEntryPoint.Onboarding,
        });
    },
    selectSport: async (sport: Sport) => {
      const newAnswer: Answer = { ...answer, sport };
      setAnswer(newAnswer);
      submitEvent({
        type: AnalyticsEventType.OnboardingSportSelected,
        sport: sport,
      });
      await complete(newAnswer);
    },
    selectRole: async (role: UserRole) => {
      const newAnswer: Answer = { ...answer, role };
      setAnswer(newAnswer);
      submitEvent({
        type: AnalyticsEventType.OnboardingRoleSelected,
        userRole: role,
      });
      await complete(newAnswer);
    },
    selectBoardPurposes: async (boardPurposes: BoardPurpose[]) => {
      const newAnswer: Answer = { ...answer, boardPurposes };
      setAnswer(newAnswer);
      submitEvent({
        type: AnalyticsEventType.OnboardingBoardPurposesSelected,
      });
      await complete(newAnswer);
    },
    selectBoardVenues: async (boardVenues: BoardVenue[]) => {
      const newAnswer: Answer = { ...answer, boardVenues };
      setAnswer(newAnswer);
      submitEvent({
        type: AnalyticsEventType.OnboardingBoardVenuesSelected,
      });
      await complete(newAnswer);
    },
  };
}

function useComplete(): useCompleteReturn {
  const { user } = useUser();
  const { updateUser } = useMutateUser();
  const { selectBoardType } = useSelectBoardType();
  const { submitEvent } = useAnalyticsEvent();

  return async (answer: Answer) => {
    const { sport, role, boardPurposes, boardVenues } = answer;

    if (
      sport === undefined ||
      role === undefined ||
      boardPurposes === undefined ||
      boardVenues === undefined
    )
      return;

    submitEvent({
      type: AnalyticsEventType.OnboardingCompleted,
      sport,
      userRole: role,
    });

    const newUser: User = {
      ...user,
      primarySport: sport,
      role,
      boardPurposes,
      boardVenues,
    };

    await Promise.all([
      updateUser(newUser),
      selectBoardType(getSportFullBoardType(sport)),
    ]);
  };
}

type useCompleteReturn = (answer: Answer) => Promise<void>;

function useRequired(): boolean {
  const { boardType } = useSelectBoardType();
  return boardType === undefined;
}

export type useOnboardingReturn = {
  onboardingRequired?: boolean;
  role?: UserRole;
  selectUserType: (userType: OnboardingUserType) => void;
  selectSport: (sport: Sport) => Promise<void>;
  selectRole: (role: UserRole) => Promise<void>;
  selectBoardPurposes: (purposes: BoardPurpose[]) => Promise<void>;
  selectBoardVenues: (venues: BoardVenue[]) => Promise<void>;
};

const useStore = create<StoreState>((set) => ({
  answer: {},
  setAnswer: (answer: Answer) => set({ answer }),
}));

type StoreState = {
  answer: Answer;
  setAnswer: (answer: Answer) => void;
};

type Answer = {
  sport?: Sport;
  role?: UserRole;
  boardPurposes?: BoardPurpose[];
  boardVenues?: BoardVenue[];
};
